import PublicIcon from '@mui/icons-material/Public';
import {
	Autocomplete,
	Box,
	Card,
	debounce,
	Dialog,
	IconButton,
	MenuItem,
	Paper,
	styled,
	TextField,
	Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { API } from '~/api';
import { UIService } from '~/service/service.ui';
import { UserCountrySource } from '~/shared/dto/model.enums';
import { useService } from '~/shared/service/service.base';
import { Col, Row } from '../view.box';

export type DashboardCountriesChartProps = {
	source: UserCountrySource | undefined;
	onSourceChange?: (source: UserCountrySource | undefined) => void;
	countries: API.AnalyticsCountry[];
	height?: number;
};
export const DashboardCountriesChart: FC<DashboardCountriesChartProps> = observer(
	({ countries, source, onSourceChange, height = 600 }) => {
		const [ui] = useService(UIService);
		const [filters, setFilters] = useState<API.AnalyticsCountry[]>([]);
		const handleChangeFilters = useCallback((_: unknown, value: API.AnalyticsCountry[]) => {
			setFilters(value);
		}, []);

		const [minUsers, setMinUsers] = useState<number>(0);
		const debouncedChangeMinUsers = useMemo(() => debounce((value: number) => setMinUsers(value), 300), []);

		const [minUsersValue, setMinUsersValue] = useState<number>(0);

		const handleChangeMinUsers = useCallback(
			(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				const newMinUsers = parseInt(e.target.value, 10);
				const valueToSet = isNaN(newMinUsers) ? 0 : newMinUsers;
				setMinUsersValue(valueToSet);
				debouncedChangeMinUsers?.(valueToSet);
			},
			[debouncedChangeMinUsers],
		);

		const dataset = useMemo(() => {
			return countries
				.sort((a, b) => b.count - a.count)
				.filter(country => !filters.some(filter => filter.code === country.code) && country.count >= minUsers)
				.map(country => {
					const item: API.AnalyticsCountry & Record<string, any> = { ...country };
					return item;
				});
		}, [countries, filters, minUsers]);

		const { totalCountries, totalUsers } = useMemo(
			() => ({
				totalCountries: dataset.length,
				totalUsers: dataset.reduce((sum, country) => sum + country.count, 0),
			}),
			[dataset],
		);

		const handleOpenMapDialog = useCallback(() => {
			ui.showDialog(DashboardCountriesMapDialog);
		}, [ui]);

		return (
			<Card elevation={2} sx={{ flex: 1 }}>
				<Col p={2}>
					<BarChart
						height={height}
						dataset={dataset}
						layout="horizontal"
						series={[{ dataKey: 'count', label: `${totalUsers} users from ${totalCountries} countries` }]}
						yAxis={[{ scaleType: 'band', dataKey: 'name', tickLabelStyle: { display: 'none' } }]}
					/>
					<Row gap={2} flex={1} justifyContent="space-between">
						<Col>
							<Typography align="center" variant="caption" color="textSecondary">
								Map
							</Typography>
							<IconButton style={{ padding: 4 }} onClick={handleOpenMapDialog}>
								<PublicIcon />
							</IconButton>
						</Col>
						<DashboardCountriesSource source={source} onSourceChange={onSourceChange} />
						<TextField
							type="number"
							label="Min users"
							value={minUsersValue}
							onChange={handleChangeMinUsers}
							style={{ maxWidth: 100 }}
						/>
						<Autocomplete
							sx={{ flex: 3 }}
							multiple
							options={countries}
							renderInput={props => <TextField {...props} label="Exclude countries from chart" />}
							getOptionLabel={option => option.name}
							isOptionEqualToValue={(option, value) => option.code === value.code}
							value={filters}
							onChange={handleChangeFilters}
						/>
					</Row>
				</Col>
				<DashboardCountriesMapDialog countries={countries} source={source} onSourceChange={onSourceChange} />
			</Card>
		);
	},
);

const DashboardCountriesMapDialog: FC<DashboardCountriesChartProps> = observer(props => {
	const [ui] = useService(UIService);
	const open = useMemo(() => ui.dialog === DashboardCountriesMapDialog, [ui.dialog]);

	return (
		<Dialog open={open} fullWidth maxWidth="lg" onClose={ui.hideDialog}>
			<DashboardCountriesMap {...props} />
		</Dialog>
	);
});

const StyledPaper = styled(Paper)({
	height: 700,
	width: '100%',
	overflow: 'hidden',
	'& .leaflet-container': {
		height: '100%',
		width: '100%',
	},
});

const DashboardCountriesMap: FC<DashboardCountriesChartProps> = observer(props => {
	return (
		<Card elevation={2} sx={{ flex: 1 }}>
			<Col p={2} flex={1} gap={2}>
				<StyledPaper elevation={0}>
					<MapContainer center={{ lat: 47, lng: 13 }} zoom={5}>
						<TileLayer
							attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
							url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
						/>
						<MapCluster countries={props.countries} />
					</MapContainer>
				</StyledPaper>
				<Row gap={2} flex={1} justifyContent="space-between">
					<DashboardCountriesSource source={props.source} onSourceChange={props.onSourceChange} />
				</Row>
			</Col>
		</Card>
	);
});

const DashboardCountriesSource: FC<Pick<DashboardCountriesChartProps, 'source' | 'onSourceChange'>> = observer(
	({ source, onSourceChange }) => {
		const { t } = useTranslation('dto');
		const handleChangeSource = useCallback(
			(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				const newSource = e.target.value === 'all' ? undefined : (e.target.value as UserCountrySource);
				onSourceChange?.(newSource);
			},
			[onSourceChange],
		);
		return (
			<TextField sx={{ flex: 1 }} select label="Source" value={source || 'all'} onChange={handleChangeSource}>
				<MenuItem value={'all'}>All</MenuItem>
				{Object.values(UserCountrySource).map(value => (
					<MenuItem key={value} value={value}>
						{t(`UserCountrySource.${value}`)}
					</MenuItem>
				))}
			</TextField>
		);
	},
);

const formatMarkerCount = (count: number): string => {
	if (count < 1000) return String(count);
	return `${Math.floor(count / 1000)}k`;
};

const CountryMarkerIcon: FC<{ count: number }> = ({ count }) => (
	<Box
		style={{
			width: 56,
			height: 56,
			position: 'relative',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		}}
	>
		<Box
			style={{
				width: 40,
				height: 40,
				borderRadius: '50% 50% 50% 0',
				transform: 'rotate(-45deg)',
				background: '#1976d2',
				border: '3px solid #ffffff',
				boxShadow: '0 3px 11px rgba(0,0,0,0.35)',
			}}
		/>
		<Typography
			style={{
				position: 'absolute',
				color: '#ffffff',
				fontSize: 15,
				fontWeight: 700,
				lineHeight: 1,
				top: 6,
				left: '50%',
				transform: 'translateX(-50%)',
				whiteSpace: 'nowrap',
				pointerEvents: 'none',
			}}
		>
			{formatMarkerCount(count)}
		</Typography>
	</Box>
);

const ClusterMarkerIcon: FC<{ count: number; size: number }> = ({ count, size }) => {
	const fontSize = size >= 78 ? 22 : size >= 66 ? 18 : 16;
	const borderWidth = size >= 78 ? 5 : size >= 66 ? 4 : 4;
	const shadowBlur = size >= 78 ? 15 : size >= 66 ? 13 : 12;

	return (
		<Box
			style={{
				width: size,
				height: size,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				borderRadius: '50%',
				background: 'linear-gradient(180deg, #fde047 0%, #eab308 100%)',
				border: `${borderWidth}px solid #ffffff`,
				boxShadow: `0 2px ${shadowBlur}px rgba(0,0,0,0.4), 0 0 0 2px rgba(234,179,8,0.35)`,
			}}
		>
			<Typography
				style={{
					color: '#1f2937',
					fontSize,
					fontWeight: 800,
					lineHeight: 1,
					whiteSpace: 'nowrap',
					pointerEvents: 'none',
				}}
			>
				{formatMarkerCount(count)}
			</Typography>
		</Box>
	);
};

const MapMarker: FC<{ country: API.AnalyticsCountry; onClick?: (country: API.AnalyticsCountry) => void }> = ({
	country,
	onClick,
}) => {
	const countryMarkerIcon = useMemo(
		() =>
			L.divIcon({
				html: renderToStaticMarkup(<CountryMarkerIcon count={country.count} />),
				className: 'country-marker-icon',
				iconSize: [56, 56],
				iconAnchor: [28, 53],
			}),
		[country.count],
	);

	const handleClick = useCallback(() => {
		onClick?.(country);
	}, [country, onClick]);

	return (
		<Marker icon={countryMarkerIcon} position={[country.lat, country.lng]} eventHandlers={{ click: handleClick }}>
			<Popup>
				{country.name}: {country.count} users
			</Popup>
		</Marker>
	);
};

const MapCluster: FC<Pick<DashboardCountriesChartProps, 'countries'>> = observer(({ countries }) => {
	const renderObject = useCallback((c: API.AnalyticsCountry) => {
		return <MapMarker key={c.code} country={c} onClick={c => console.log('Click:', c)} />;
	}, []);

	const createClusterIcon = useCallback((cluster: { getChildCount: () => number }) => {
		const count = cluster.getChildCount();
		const baseSize = count > 100 ? 52 : count > 25 ? 44 : 36;
		const size = Math.round(baseSize * 1.5);

		return L.divIcon({
			html: renderToStaticMarkup(<ClusterMarkerIcon count={count} size={size} />),
			className: 'country-marker-cluster-icon',
			iconSize: [size, size],
			iconAnchor: [size, size],
		});
	}, []);

	return (
		<MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterIcon}>
			{countries.map(renderObject)}
		</MarkerClusterGroup>
	);
});
